import { AxiosResponse } from 'axios'
import { http } from '../realisations/Http.realisation'
import {
    BillingGetPeriodsResponseT,
    EmployeePayloadT,
    ProfileResponseT,
    UpdateUserPayloadT,
    UserPayloadT,
    UsersChangeStatusPayload,
    UsersCreateEmployeeResponseT,
    UsersCreateUserResponseT,
    UsersGetDictionaryResponseT,
    UsersGetEmployeeResponseT,
    UsersGetEmployeesFiltersT,
    UsersGetEmployeesResponseT,
    UsersGetInitiatorsParams,
    UsersGetInitiatorsResponseT,
    UsersGetListFilters,
    UsersGetListResponseT,
    UsersGetManagersParams,
    UsersGetManagersResponseT,
    UsersGetTokenResponseT,
    UsersGetUserResponseT,
    UsersUpdateEmployeeResponseT,
    UsersUpdateUserResponseT,
} from '../types/users.types'

export class UsersRepository {
    getList(params: UsersGetListFilters): Promise<AxiosResponse<UsersGetListResponseT>> {
        return http.get('/gateway/users', { params })
    }

    getDictionary(
        type: number,
        description?: string
    ): Promise<AxiosResponse<UsersGetDictionaryResponseT>> {
        return http.get('/cabinet/api/employee-dictionaries', { params: { type, description } })
    }

    getEmployees(
        params: UsersGetEmployeesFiltersT
    ): Promise<AxiosResponse<UsersGetEmployeesResponseT>> {
        return http.get('/cabinet/api/employees', { params })
    }

    getUserFromCabinet(user_id: number): Promise<AxiosResponse<UsersGetUserResponseT>> {
        return http.get(`/cabinet/api/v2/users/${user_id}`)
    }

    getEmployee(id: number): Promise<AxiosResponse<UsersGetEmployeeResponseT>> {
        return http.get(`/cabinet/api/employees/${id}`)
    }

    getDispatchers(
        page: number,
        name?: string,
        limit?: number
    ): Promise<AxiosResponse<UsersGetListResponseT>> {
        const params = {
            page,
            name,
            limit,
        }

        return http.get('/gateway/users?roleId=16', { params })
    }

    getAccountants(
        page: number,
        name?: string,
        statusId?: number
    ): Promise<AxiosResponse<UsersGetListResponseT>> {
        const params = {
            page,
            name,
            statusId,
        }

        return http.get('/gateway/users?roleId=23', { params })
    }

    getLogists(
        page: number,
        name?: string,
        isExtended?: number,
        statusId?: number,
        limit?: number
    ): Promise<AxiosResponse<UsersGetListResponseT>> {
        const params = {
            page,
            name,
            isExtended,
            statusId,
            limit
        }

        return http.get('/gateway/users?roleId=21', { params })
    }

    createUser(payload: UserPayloadT): Promise<AxiosResponse<UsersCreateUserResponseT>> {
        return http.post('/gateway/users', { ...payload, source: 1 })
    }

    createEmployee(
        payload: EmployeePayloadT
    ): Promise<AxiosResponse<UsersCreateEmployeeResponseT>> {
        return http.post('/cabinet/api/employees', payload)
    }

    updateUser(
        user_id: number,
        payload: UpdateUserPayloadT
    ): Promise<AxiosResponse<UsersUpdateUserResponseT>> {
        return http.put(`/gateway/users/${user_id}`, payload)
    }

    updateEmployee(
        id: number,
        payload: EmployeePayloadT
    ): Promise<AxiosResponse<UsersUpdateEmployeeResponseT>> {
        return http.put(`/cabinet/api/employees/${id}`, payload)
    }

    updateUserInCabinet(
        user_id: number,
        payload: UpdateUserPayloadT
    ): Promise<AxiosResponse<UsersUpdateUserResponseT>> {
        return http.put(`/cabinet/api/v2/users/update/${user_id}`, payload)
    }

    deleteUser(user_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/gateway/users/${user_id}`)
    }

    deleteEmployee(id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/cabinet/api/employees/${id}`)
    }

    getManagers(params: UsersGetManagersParams): Promise<AxiosResponse<UsersGetManagersResponseT>> {
        return http.get('/gateway/users', {
            params: {
                ...params,
                limit: 9999,
                roleId: 20,
            },
        })
    }

    getInitiators(
        params: UsersGetInitiatorsParams
    ): Promise<AxiosResponse<UsersGetInitiatorsResponseT>> {
        return http.get('/gateway/users', {
            params: {
                ...params,
                limit: 9999,
                roleIds: [15, 20],
            },
        })
    }

    getBillingPeriods(page: number): Promise<AxiosResponse<BillingGetPeriodsResponseT>> {
        const params = {
            page,
        }

        return http.get('/cabinet/api/billing-periods', { params })
    }

    getUserToken(user_id: number): Promise<AxiosResponse<UsersGetTokenResponseT>> {
        return http.post('/gateway/impersonate', { user_id })
    }

    changeStatus(payload: UsersChangeStatusPayload): Promise<AxiosResponse<void>> {
        return http.post('/gateway/users/change-status', payload)
    }

    getProfile(): Promise<AxiosResponse<ProfileResponseT>> {
        return http.get('/cabinet/api/v2/users/profile')
    }
}
